import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { PromisePool } from 'es6-promise-pool';

import {
  setUploadBatchId,
  setUploadPageDragDropEnabled,
} from '../../actions/UploadPages';

import { APIcreateFolderCollections } from '../../api/Collections';
import {
  APIcreateFilesGroup,
  APIfinishFilesGroup,
  APIupdateFile,
} from '../../api/Files';

import UploadPageFileGrid from './FileGrid';
import UploadPageUploadButton from './UploadButton';
import UploadPageActionBar from './ActionBar';
import UploadPageFinished from './Finished';

import { getVisitorId } from '../../helpers/Auth';
import withUploader from '../../helpers/withUploader';

const UploadPageUploader = ({
  setDragDropEnabled,
  resetUploader,
  uploadPage,
  uploader,
  submittedFiles,
  selectedFiles,
  setSelectedFiles,
  switchSelectFile,
  checkUploadLimit,
  storageLimitExceeded,
  resetUploadPage,
  setFileParams,
  setBatchId,
  handleRetryFile,
}) => {
  const [uploadUuid, setUploadUuid] = useState(null);
  const [folders, setFolders] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedFiles, setSavedFiles] = useState([]);
  const [finishing, setFinishing] = useState(false);
  const [finished, setFinished] = useState(false);

  const completedFiles = submittedFiles.filter(sf => (sf.uploadStatus === 'completed')).length;
  const incompletedFiles = submittedFiles.length - completedFiles;

  const [uploadedMap, setUploadedMap] = useState({});
  const [totalMap, setTotalMap] = useState({});

  useEffect(() => {
    const handleSubmitted = (fileId) => {
      setSaving(true);
      const file = uploader.methods.getFile(fileId);
      if (file?.size != null) {
        setTotalMap(prev => ({ ...prev, [fileId]: file.size }));
      }
    };

    uploader.on('submitted', handleSubmitted);
    return () => uploader.off('submitted', handleSubmitted);
  }, [uploader]);

  const handleProgress = useCallback((fileId, name, bytesUploaded) => {
    setUploadedMap(prev => ({
      ...prev,
      [fileId]: bytesUploaded * 0.8,
    }));
  }, []);

  useEffect(() => {
    uploader.on('progress', handleProgress);
    return () => uploader.off('progress', handleProgress);
  }, [uploader, handleProgress]);

  const overallProgress = useMemo(() => {
    const uploaded = Object.values(uploadedMap).reduce((sum, val) => sum + val, 0);
    const total = Object.values(totalMap).reduce((sum, val) => sum + val, 0);
    return total > 0 ? Math.min(100, Math.round((uploaded / total) * 100)) : 0;
  }, [uploadedMap, totalMap]);

  const resetUpload = () => {
    resetUploader();
    setUploadUuid(null);
    setFinished(false);
    setDragDropEnabled(true);
    setSavedFiles([]);
  };

  const submitFiles = (files) => {
    if (finished) {
      resetUpload();
    }
    setBatchId();
    uploader.methods.addFiles(files);
  };

  const handleSelectAllFiles = () => {
    setSelectedFiles(submittedFiles.map(sf => (sf.id)));
  };

  const handleUnselectAllFiles = () => {
    setSelectedFiles([]);
  };

  const handleRemoveSelectedFiles = () => {
    let counterFiles = 0;
    const removedFiles = [];
    const promiseProducerFiles = () => {
      if (counterFiles < selectedFiles.length) {
        const file = submittedFiles.find(sf => (sf.id === selectedFiles[counterFiles]));
        if (file) {
          uploader.methods.cancel(file.id);
          removedFiles.push(file.id);
        }
        counterFiles += 1;
        return Promise.resolve();
      }
      return null;
    };

    const pool = new PromisePool(promiseProducerFiles, 1);
    pool.start().then(() => {
      handleUnselectAllFiles();
    });
  };

  const setFileFolders = useCallback(() => {
    const fileFolders = submittedFiles.filter(sf => (sf.folder)).map(sf => (sf.folder.replace('\\', '/')));
    setFolders([...new Set(fileFolders)]);
  }, [submittedFiles]);

  const handleSaveFiles = (folderCollections = []) => {
    const getFolderCollection = (file) => (
      (file.folder && folderCollections[file.folder]) ?
        [folderCollections[file.folder]] :
        [{ uuid: uploadPage.collection?.uuid }]
    );

    let counter = 0;
    const promiseProducer = () => {
      if (counter < submittedFiles.length) {
        const file = submittedFiles[counter];
        counter += 1;
        if (file.uploadStatus === 'failed' || savedFiles.includes(file.uuid)) {
          return Promise.resolve();
        }
        return APIupdateFile({
          ...file,
          public: true,
          status: !uploadPage.file_status ? -1 : uploadPage.file_status,
          searchable: true,
          uploadStatus: 'completed',
          uploadUuid,
          upload_page_uuid: uploadPage.uuid,
          vid: getVisitorId(),
          mapping: true,
          collections: getFolderCollection(file),
        }).then(() => {
          const uploadedFile = uploader.methods.getFile(file.id);

          setSavedFiles(prev => [...prev, file.uuid]);
          setUploadedMap(prev => ({
            ...prev,
            [file.id]: uploadedFile.size,
          }));
        });
      }
      return null;
    };

    const pool = new PromisePool(promiseProducer, 2);
    pool.start().then(() => {
      setFinishing(true);
      APIfinishFilesGroup(uploadUuid, {
        data: {
          upload_page_uuid: uploadPage.uuid,
        },
      }).then(() => {
        setSaving(false);
        checkUploadLimit();
        setFinishing(false);
        setDragDropEnabled(true);
      });
    });
  };

  const handleDone = () => {
    setFinished(true);
    resetUploadPage();
  };

  const handleRetryAllFailed = () => {
    submittedFiles.filter(sf => sf.uploadStatus === 'failed').forEach(sf => {
      handleRetryFile(sf.id);
    });
  };

  useEffect(() => {
    if (submittedFiles.find(f => f.uploadStatus === 'uploading')) return;

    if (uploadUuid) {
      if (folders.length === 0) {
        handleSaveFiles();
      } else {
        APIcreateFolderCollections({
          folders,
          parent_collection_uuid: uploadPage.collection?.uuid,
          upload_page_uuid: uploadPage.uuid,
        }).then((res) => {
          handleSaveFiles(res.data);
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadPage, uploadUuid, folders, submittedFiles]);

  useEffect(() => {
    setFileParams({
      upload_page_uuid: uploadPage.uuid,
      collections: [{ uuid: uploadPage.collection?.uuid }],
    });
  }, [uploadPage, setFileParams]);

  const handleSave = () => {
    setDragDropEnabled(false);
    handleUnselectAllFiles();

    if (storageLimitExceeded) {
      setSaving(false);
      setDragDropEnabled(true);
      return;
    }

    APIcreateFilesGroup({
      type: 'upload',
      data: {
        upload_page_uuid: uploadPage.uuid,
      },
    }).then((response) => {
      const { data } = response;
      if (data) {
        setFileFolders();
        setUploadUuid(data.uuid);
      }
    });
  };

  useEffect(() => {
    if (!uploader) return () => {};

    const callback = () => {
      handleSave();
    };

    uploader.on('allComplete', callback);

    return () => {
      if (uploader.off) {
        uploader.off('allComplete', callback);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploader, submittedFiles]);

  return (
    <div>
      {finished && (
        <UploadPageFinished
          uploadPageUuid={uploadPage.uuid}
          uploadUuid={uploadUuid}
          files={submittedFiles.length}
          resetUpload={resetUpload}
        />
      )}
      <div
        className={classnames('upload-area', {
          'has-files': submittedFiles.length > 0 && !finished,
          bordered: !(submittedFiles.length > 0 && !finished),
          finished,
        })}
      >
        <UploadPageUploadButton
          submittedFilesCount={submittedFiles.length}
          submitFiles={submitFiles}
          finished={finished}
          disabled={saving || finishing}
        />
        {!finished && (
          <UploadPageFileGrid
            uploader={uploader}
            saving={saving}
            submittedFiles={submittedFiles}
            selectedFiles={selectedFiles}
            switchSelectFile={switchSelectFile}
            handleRetryFile={handleRetryFile}
          />
        )}
      </div>
      {(submittedFiles.length > 0 && !finished && !storageLimitExceeded) && (
        <UploadPageActionBar
          submittedFiles={submittedFiles}
          selectedFiles={selectedFiles}
          savedFiles={savedFiles}
          saving={saving}
          finishing={finishing}
          incompletedFiles={incompletedFiles}
          handleSelectAllFiles={handleSelectAllFiles}
          handleUnselectAllFiles={handleUnselectAllFiles}
          handleRemoveSelectedFiles={handleRemoveSelectedFiles}
          handleDone={handleDone}
          handleRetryAllFailed={handleRetryAllFailed}
          overallProgress={overallProgress}
        />
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  uploadPage: state.UploadPages.uploadPage,
});

const mapDispatchToProps = dispatch => ({
  setDragDropEnabled: (dragDropEnabled) => {
    dispatch(setUploadPageDragDropEnabled(dragDropEnabled));
  },
  setBatchId: () => {
    dispatch(setUploadBatchId());
  },
});

UploadPageUploader.propTypes = {
  setDragDropEnabled: PropTypes.func.isRequired,
  uploadPage: PropTypes.object.isRequired,
  resetUploader: PropTypes.func.isRequired,
  uploader: PropTypes.object.isRequired,
  submittedFiles: PropTypes.array.isRequired,
  selectedFiles: PropTypes.array.isRequired,
  storageLimitExceeded: PropTypes.bool.isRequired,
  setSelectedFiles: PropTypes.func.isRequired,
  switchSelectFile: PropTypes.func.isRequired,
  checkUploadLimit: PropTypes.func.isRequired,
  resetUploadPage: PropTypes.func.isRequired,
  setFileParams: PropTypes.func.isRequired,
  setBatchId: PropTypes.func.isRequired,
  handleRetryFile: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withUploader(UploadPageUploader));
